import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { newId, pendingOps, useOutbox, type Message, type Request } from '@dia/db';
import { useT } from '@dia/i18n';
import { Button } from '@dia/ui';
import { RequireAuth, type Identity } from './lib/identity';
import { Icon } from './lib/icons';
import { loadMessages, loadStaffDirectory, loadStaysToday, translateText, useLoad } from './lib/data';
import { loadRequest, useLateSpanish } from './lib/requests';
import { declinePatch, statusPatch, statusQueued, WORK_STEPS, type WorkStep } from './lib/requestWork';
import { messageText, requestText, type Lang } from './lib/text';
import { formatDay, formatTime } from './lib/time';
import { whatsappLink } from './lib/pairing';
import { Card, Field, inputClass, Notice, Page, Pill } from './lib/ui';

/**
 * The thread for one request. A message board with a translation shadow, not a
 * chat: no typing dots, no read receipts, polling every 30 seconds. Each
 * message is kept in the language it was written in, with the other language
 * beside it, and a tap shows the original.
 *
 * On a request it is also where the request is worked (migration 022): three
 * buttons move requests.status, the same column the guest's card reads, and a
 * host can answer the guest right under that card, which stores the reply in
 * the stay thread with the request's id on it.
 *
 * Working the queue, declining and answering a guest are a manager's job
 * (CLAUDE.md Section 4, "guest requests"), so those controls show for a
 * manager or an owner. The crew read the request and write in the internal
 * staff thread under it. Migration 023 enforces the same rule.
 */
export function MensajesHilo() {
  return <RequireAuth>{(who) => <ThreadBody who={who} />}</RequireAuth>;
}

function ThreadBody({ who }: { who: Identity }) {
  const { requestId, stayId } = useParams();
  const kind = stayId ? 'stay' : 'request';
  const threadId = stayId ?? requestId ?? '';
  const { t, lang } = useT();
  const { enqueue, pending } = useOutbox();
  const requestLoad = useLoad(
    () => (kind === 'request' ? loadRequest(threadId) : Promise.resolve(null)),
    `request:${threadId}:${pending}`,
    30_000,
  );
  const stays = useLoad(loadStaysToday, 'stays');
  const messages = useLoad(() => loadMessages(threadId, kind), `msgs:${kind}:${threadId}:${pending}`, 30_000);
  const directory = useLoad(loadStaffDirectory, 'directory');
  const audience = kind === 'stay' ? 'guest' : who.role === 'owner' ? 'owner' : 'staff';
  // A manager or an owner works the guest queue. Crew report and read.
  const boss = who.role !== 'crew';
  const statusWaiting = useLoad(
    async () => statusQueued(threadId, await pendingOps()),
    `request-waiting:${threadId}:${pending}`,
  );

  // A message sent with no signal has no shadow, and the database will not let
  // anyone add one later. So the reader translates it on read, once, when
  // there is signal, and the server cache makes a repeat free.
  const [onRead, setOnRead] = useState<Record<string, string>>({});
  const tried = useRef<Set<string>>(new Set());
  useEffect(() => {
    for (const m of (messages.data ?? []).slice(-12)) {
      const key = `${m.id}:${lang}`;
      const shadow = lang === 'es' ? m.text_es : m.text_en;
      if (m.lang_original === lang || shadow || tried.current.has(key)) continue;
      tried.current.add(key);
      void translateText(m.text_original, m.lang_original, lang, who.token, audience, 8000).then((r) => {
        if (r.ok) setOnRead((prev) => ({ ...prev, [key]: r.text }));
        else tried.current.delete(key);
      });
    }
  }, [messages.data, lang, who.token, audience]);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [showOriginal, setShowOriginal] = useState<Set<string>>(new Set());

  const request = requestLoad.data ?? undefined;
  const stay = kind === 'stay' ? stays.data?.find((s) => s.id === threadId) : undefined;

  // A request that reached the island in English only gets its Spanish here
  // too, the first time somebody opens it with signal.
  const asList = useMemo(() => (request ? [request] : null), [request]);
  useLateSpanish(asList, who.token, enqueue);

  // A guest's request, answered by a host: the reply goes to the guest.
  const guestRequest = request && request.created_by_guest && request.stay_id ? request : null;
  const canReplyToGuest = guestRequest !== null && boss;
  const nameOf = (id: string | null) => directory.data?.find((p) => p.id === id)?.name ?? t('ops.messages.author_team');

  // Messages are append only in the database, so the translated shadow has to
  // travel with the message. It is asked for only when there is signal, for at
  // most five seconds. With no signal the message is queued at once in its own
  // language and shows as "not translated yet" to the reader.
  const post = async (
    body: string,
    to: { threadKind: 'stay' | 'request'; stayId: string | null; requestId: string | null },
    readers: 'guest' | 'owner' | 'staff',
  ) => {
    const other: Lang = lang === 'es' ? 'en' : 'es';
    const result = await translateText(body, lang, other, who.token, readers, 5000);
    const shadow = result.ok ? result.text : null;
    await enqueue({
      id: newId(),
      table: 'messages',
      payload: {
        thread_kind: to.threadKind,
        request_id: to.requestId,
        stay_id: to.stayId,
        author_kind: 'staff',
        author_staff_id: who.staffId,
        text_original: body,
        lang_original: lang,
        text_es: lang === 'es' ? body : shadow,
        text_en: lang === 'en' ? body : shadow,
        created_at: new Date().toISOString(),
      },
    });
  };

  const send = async () => {
    const body = text.trim();
    if (!body) return;
    setBusy(true);
    try {
      await post(
        body,
        {
          threadKind: kind,
          requestId: kind === 'request' ? threadId : null,
          stayId: kind === 'stay' ? threadId : null,
        },
        audience,
      );
      setText('');
      await messages.reload();
    } finally {
      setBusy(false);
    }
  };

  // The guest's side of the same request: their stay thread, filtered to the
  // replies that name this request.
  const guestStayId = canReplyToGuest ? guestRequest.stay_id : null;
  const guestThread = useLoad(
    () => (guestStayId ? loadMessages(guestStayId, 'stay') : Promise.resolve([] as Message[])),
    `msgs:stay:${guestStayId ?? '-'}:${pending}`,
    30_000,
  );
  const replies = (guestThread.data ?? []).filter((m) => request && m.request_id === request.id);
  const [reply, setReply] = useState('');
  const [replyBusy, setReplyBusy] = useState(false);
  const [declineToo, setDeclineToo] = useState(false);
  const canDecline = boss && guestRequest !== null && guestRequest.status !== 'declined';
  const sendReply = async () => {
    const body = reply.trim();
    if (!body || !guestRequest || !guestRequest.stay_id) return;
    setReplyBusy(true);
    try {
      await post(body, { threadKind: 'stay', stayId: guestRequest.stay_id, requestId: guestRequest.id }, 'guest');
      if (declineToo && canDecline) {
        await enqueue({
          table: 'requests',
          kind: 'update',
          payload: declinePatch(who.staffId, new Date()),
          match: { id: guestRequest.id },
        });
        await requestLoad.reload();
      }
      setReply('');
      setDeclineToo(false);
      await guestThread.reload();
    } finally {
      setReplyBusy(false);
    }
  };

  const [stepBusy, setStepBusy] = useState<WorkStep | null>(null);
  const moveTo = async (step: WorkStep) => {
    if (!request || request.status === step) return;
    setStepBusy(step);
    try {
      await enqueue({
        table: 'requests',
        kind: 'update',
        payload: statusPatch(step, who.staffId, new Date()),
        match: { id: request.id },
      });
      await requestLoad.reload();
    } finally {
      setStepBusy(null);
    }
  };

  const toggle = (id: string) =>
    setShowOriginal((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <Page title={kind === 'stay' ? t('ops.messages.stay_thread') : t('ops.messages.thread_title')} back="/mensajes">
      {request ? (
        <Card className="mb-4">
          <p className="m-0 font-semibold">{t(`ops.request.type.${request.type}`)}</p>
          <p className="m-0">{requestText(request, lang)}</p>
          <a
            href={whatsappLink(requestText(request, lang))}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-2 inline-flex min-h-tap items-center gap-2 text-accent underline underline-offset-4"
          >
            <Icon name="chat" /> {t('ops.messages.whatsapp')}
          </a>
          <WorkControl
            request={request}
            canWork={boss}
            waiting={statusWaiting.data === true}
            busy={stepBusy}
            onStep={(step) => void moveTo(step)}
          />
        </Card>
      ) : stay ? (
        <Card className="mb-4">
          <p className="m-0 font-semibold">
            {t('ops.stays.dates_line', { from: formatDay(stay.arrives_on, lang), to: formatDay(stay.departs_on, lang) })}
          </p>
          <p className="m-0">{t('ops.stays.people_line', { adults: stay.adults, children: stay.children })}</p>
        </Card>
      ) : (
        !requestLoad.loading &&
        !stays.loading && (
          <Notice tone="warn">{kind === 'stay' ? t('ops.messages.stay_missing') : t('ops.messages.request_missing')}</Notice>
        )
      )}

      {canReplyToGuest && (
        <GuestReply
          replies={replies}
          loading={guestThread.loading}
          value={reply}
          onChange={setReply}
          busy={replyBusy}
          onSend={() => void sendReply()}
          lang={lang}
          nameOf={nameOf}
          myId={who.staffId}
          canDecline={canDecline}
          declineToo={declineToo}
          onDeclineTooChange={setDeclineToo}
        />
      )}

      <p className="mb-2 text-inkMuted">{t('ops.messages.updates')}</p>
      {guestRequest && <p className="mb-2 font-semibold">{t('ops.messages.team_only')}</p>}

      {(messages.data ?? []).length === 0 && !messages.loading && (
        <Notice tone="info">{t('ops.messages.empty_thread')}</Notice>
      )}

      <ul className="m-0 mb-4 flex list-none flex-col gap-3 p-0">
        {(messages.data ?? []).map((m) => {
          const filled = onRead[`${m.id}:${lang}`];
          const shown = messageText(
            filled ? { ...m, text_es: lang === 'es' ? filled : m.text_es, text_en: lang === 'en' ? filled : m.text_en } : m,
            lang,
          );
          const mine = m.author_staff_id !== null && m.author_staff_id === who.staffId;
          const reveal = showOriginal.has(m.id);
          return (
            <li key={m.id} className={`rounded border p-3 ${mine ? 'border-accent bg-surface' : 'border-line bg-surface'}`}>
              <p className="m-0 font-semibold">
                {mine ? t('ops.messages.author_me') : m.author_kind === 'guest' ? t('ops.messages.author_guest') : nameOf(m.author_staff_id)}{' '}
                <span className="font-normal text-inkMuted">{formatTime(m.created_at, lang)}</span>
              </p>
              {kind === 'stay' && m.request_id && (
                <p className="m-0 mt-1 text-inkMuted">{t('ops.messages.reply_to_request')}</p>
              )}
              <p className="m-0 mt-1">{reveal ? shown.original : shown.text}</p>
              {m.lang_original !== lang && (
                <>
                  {!shown.translated && <p className="m-0 mt-1 text-inkMuted">{t('ops.messages.not_translated')}</p>}
                  {shown.translated && (
                    <button
                      type="button"
                      onClick={() => toggle(m.id)}
                      className="mt-1 inline-flex min-h-tap items-center text-accent underline underline-offset-4"
                    >
                      {reveal ? t('lang.show_translation') : t('lang.show_original')}
                    </button>
                  )}
                </>
              )}
            </li>
          );
        })}
      </ul>

      {/* A stay thread is the guest's own conversation with the hosts: only a
          manager or an owner writes in it. The internal thread under a request
          is open to the whole team. */}
      {kind === 'stay' && !boss ? (
        <Notice tone="info">{t('ops.messages.stay_hosts_only')}</Notice>
      ) : (
        <div className="flex flex-col gap-3">
          <Field label={t('ops.messages.write')}>
            <textarea value={text} onChange={(e) => setText(e.target.value)} rows={3} className={inputClass} />
          </Field>
          <Button size="lg" block busy={busy} busyLabel={t('loading')} disabled={!text.trim()} onClick={() => void send()}>
            <Icon name="send" /> {t('ops.messages.send')}
          </Button>
        </div>
      )}
    </Page>
  );
}

/**
 * Three big buttons that move requests.status, the column the guest card
 * reads. The current step is marked by its word and a check, never by color
 * alone, and a tap goes through the outbox like every other write, so it works
 * with no signal and says so until the island has it.
 */
function WorkControl({
  request,
  canWork,
  waiting,
  busy,
  onStep,
}: {
  request: Request;
  canWork: boolean;
  waiting: boolean;
  busy: WorkStep | null;
  onStep: (step: WorkStep) => void;
}) {
  const { t } = useT();
  const now = t(`ops.request.status.${request.status}`);
  return (
    <section className="mt-4 flex flex-col gap-3 border-t border-line pt-4" aria-label={t('ops.request.work_title')}>
      <h2 className="m-0 text-[1.1em] font-semibold">{t('ops.request.work_title')}</h2>
      <p className="m-0 flex flex-wrap items-center gap-2">
        <Pill tone={request.status === 'done' ? 'ok' : request.status === 'in_progress' ? 'warn' : 'info'}>
          {t('ops.request.work_now', { status: now })}
        </Pill>
        {waiting && <span className="text-inkMuted">{t('sync.saved_pending')}</span>}
      </p>
      {canWork ? (
        <div className="flex flex-col gap-2">
          {WORK_STEPS.map((step) => {
            const current = request.status === step;
            return (
              <Button
                key={step}
                size="lg"
                block
                variant={current ? 'primary' : 'secondary'}
                aria-pressed={current}
                busy={busy === step}
                busyLabel={t('loading')}
                disabled={busy !== null}
                onClick={() => onStep(step)}
              >
                {current && <Icon name="check" />} {t(`ops.request.work.${step}`)}
              </Button>
            );
          })}
          {request.created_by_guest && <p className="m-0 text-inkMuted">{t('ops.request.work_guest_sees')}</p>}
        </div>
      ) : (
        <p className="m-0 text-inkMuted">{t('ops.request.work_hosts_only')}</p>
      )}
    </section>
  );
}

/**
 * Answering the guest under their request. The reply is a stay message with
 * the request's id on it, so the guest's thread draws it right under their
 * card instead of somewhere later in time order.
 */
function GuestReply({
  replies,
  loading,
  value,
  onChange,
  busy,
  onSend,
  lang,
  nameOf,
  myId,
  canDecline,
  declineToo,
  onDeclineTooChange,
}: {
  replies: Message[];
  loading: boolean;
  value: string;
  onChange: (next: string) => void;
  busy: boolean;
  onSend: () => void;
  lang: Lang;
  nameOf: (id: string | null) => string;
  myId: string | null;
  canDecline: boolean;
  declineToo: boolean;
  onDeclineTooChange: (next: boolean) => void;
}) {
  const { t } = useT();
  return (
    <section className="mb-6 flex flex-col gap-3 rounded border border-accent bg-surface p-4">
      <h2 className="m-0 text-[1.15em] font-semibold">{t('ops.messages.reply_guest_title')}</h2>
      <p className="m-0 text-inkMuted">{t('ops.messages.reply_guest_hint')}</p>
      {replies.length === 0 && !loading && <p className="m-0 text-inkMuted">{t('ops.messages.reply_guest_empty')}</p>}
      {replies.length > 0 && (
        <ul className="m-0 flex list-none flex-col gap-2 p-0">
          {replies.map((m) => (
            <li key={m.id} className="rounded border border-line p-3">
              <p className="m-0 font-semibold">
                {m.author_kind === 'guest'
                  ? t('ops.messages.author_guest')
                  : m.author_staff_id !== null && m.author_staff_id === myId
                    ? t('ops.messages.author_me')
                    : nameOf(m.author_staff_id)}{' '}
                <span className="font-normal text-inkMuted">{formatTime(m.created_at, lang)}</span>
              </p>
              <p className="m-0 mt-1">{messageText(m, lang).text}</p>
            </li>
          ))}
        </ul>
      )}
      <Field label={t('ops.messages.reply_guest_label')}>
        <textarea value={value} onChange={(e) => onChange(e.target.value)} rows={3} className={inputClass} />
      </Field>
      {canDecline && (
        <label className="flex min-h-tap items-center gap-2">
          <input
            type="checkbox"
            checked={declineToo}
            onChange={(e) => onDeclineTooChange(e.target.checked)}
            className="h-5 w-5"
          />
          {t('ops.messages.decline_checkbox')}
        </label>
      )}
      <Button size="lg" block busy={busy} busyLabel={t('loading')} disabled={!value.trim()} onClick={onSend}>
        <Icon name="send" /> {declineToo ? t('ops.messages.reply_guest_send_decline') : t('ops.messages.reply_guest_send')}
      </Button>
    </section>
  );
}
